import { useCallback, useEffect, useRef, useState } from 'react';
import { useMasterPlayer } from '../../smartLinks/catalog/song/player/useMasterPlayer';

export interface PreviewTransport {
  isPlaying: boolean;
  isLoading: boolean;
  /** False with no playable master copy: the preview shows still frames only. */
  canPlay: boolean;
  /** The play position of the last render (about 20 times a second while it plays). */
  positionSec: number;
  /** The exact position now: the master audio when it is loaded, else the cursor. */
  livePositionSec: () => number;
  toggle: () => void;
  /** Moves the play head. A song that does not play only moves the cursor. */
  seek: (sec: number) => void;
  /** One bar back or forward (design b1 transport). */
  stepBar: (direction: -1 | 1) => void;
}

interface UsePreviewTransportProps {
  accountId: number;
  catalogItemUuid: string;
  /** The showcase mode plays this public audio link in place of the master stream of the song. */
  audioUrl?: string;
  canPlay: boolean;
  /** The part of the song that the video covers. */
  range: { startSec: number; endSec: number };
  downbeats: readonly number[];
}

/**
 * The one clock of the live preview (spec §4.6): the master audio of the song (`masterAudio`, the
 * player of the whole app). While the audio is not loaded, a cursor holds the play head, so the
 * timeline and the preview can move with no sound. Play starts the audio at the cursor; the
 * playback stops at the end of the video.
 */
export function usePreviewTransport({
  accountId,
  catalogItemUuid,
  audioUrl,
  canPlay,
  range,
  downbeats,
}: UsePreviewTransportProps): PreviewTransport {
  const player = useMasterPlayer({ accountId, itemUuid: catalogItemUuid, streamUrl: audioUrl });
  const [cursorSec, setCursorSec] = useState(range.startSec);
  const cursorRef = useRef(range.startSec);
  const isLoaded = player.status === 'playing' || player.status === 'paused' || player.status === 'loading';
  const positionSec = isLoaded ? player.positionSec : cursorSec;

  const moveCursor = useCallback((sec: number) => {
    cursorRef.current = sec;
    setCursorSec(sec);
  }, []);

  const clamp = useCallback(
    (sec: number) => Math.min(range.endSec, Math.max(range.startSec, sec)),
    [range.endSec, range.startSec]
  );

  const { livePositionSec: playerLive, playFrom, seek: playerSeek, toggle: playerToggle } = player;
  const livePositionSec = useCallback(
    () => (isLoaded ? (playerLive() ?? cursorRef.current) : cursorRef.current),
    [isLoaded, playerLive]
  );

  const toggle = useCallback(() => {
    if (!canPlay) {
      return;
    }
    if (isLoaded) {
      playerToggle();
      return;
    }
    const from = cursorRef.current >= range.endSec - 0.05 ? range.startSec : cursorRef.current;
    playFrom(clamp(from));
  }, [canPlay, clamp, isLoaded, playFrom, playerToggle, range.endSec, range.startSec]);

  const seek = useCallback(
    (sec: number) => {
      const at = clamp(sec);
      moveCursor(at);
      if (isLoaded) {
        playerSeek(at);
      }
    },
    [clamp, isLoaded, moveCursor, playerSeek]
  );

  const stepBar = useCallback(
    (direction: -1 | 1) => {
      const now = livePositionSec();
      const target =
        direction > 0
          ? downbeats.find((sec) => sec > now + 0.05)
          : [...downbeats].reverse().find((sec) => sec < now - 0.25);
      seek(target ?? (direction > 0 ? range.endSec : range.startSec));
    },
    [downbeats, livePositionSec, range.endSec, range.startSec, seek]
  );

  // The video ends at `range.endSec`: the song plays no further in the preview.
  useEffect(() => {
    if (player.status === 'playing' && player.positionSec >= range.endSec) {
      playerToggle();
      moveCursor(range.endSec);
    }
  }, [moveCursor, player.positionSec, player.status, playerToggle, range.endSec]);

  // The cursor follows the audio, so a pause and a later stop keep the place.
  useEffect(() => {
    if (isLoaded) {
      cursorRef.current = player.positionSec;
    }
  }, [isLoaded, player.positionSec]);

  return {
    isPlaying: player.status === 'playing',
    isLoading: player.status === 'loading',
    canPlay,
    positionSec,
    livePositionSec,
    toggle,
    seek,
    stepBar,
  };
}
